import { confirmInApp } from "../common/AppDialog";
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Image as ImageIcon,
  Save,
  X,
  Sparkles,
  Loader2,
  Upload,
  Wand2,
  Type,
  Palette,
  Mic,
  MicOff,
  Plus,
  Undo2,
  Eraser,
} from "lucide-react";
import { ClipboardPaste, ClipboardCopy } from "lucide-react";
import { BACKGROUNDS } from "../../config/backgrounds";
import { callGeminiAPI, enhanceNote } from "../../services/workspaceService";
import {
  VOICE_UPLOAD_ACCEPT,
  validateVoiceFile,
  transcribeAudioFile,
  titleFromTranscript,
} from "../../services/deepgramTranscribe";
import { isPasteDump, smartPaste, titleFromPaste } from "../../utils/normalizePaste";
import { resolveLinkThumb } from "../../utils/linkThumb";
import {
  buildInlineImgTag,
  extractImageFilesFromClipboard,
  extractImageFilesFromFileList,
  readFileAsDataUrl,
  IMAGE_ACCEPT,
} from "../../utils/inlineImages";
import { useVoiceToText } from "../../hooks/useVoiceToText";
import { useEditLock } from "../../context/EditLockContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import DictationLangToggle from "../common/DictationLangToggle";
import AgentBotIcon from "../common/AgentBotIcon";
import VoiceFrequencyBars from "../common/VoiceFrequencyBars";
import { appendDictationChunk, dictationText, tentativeDisplay } from "../../utils/dictationUtils";
import { confidenceStyle, confidenceTitle } from "../../utils/confidenceStyle";
import { getVoiceCaptureStyle } from "../../config/voiceCaptureStyle";
import { useDraftAutosave, clearDraftStorage } from "../../hooks/useDraftAutosave";
import { useNeverLoseText } from "../../hooks/useNeverLoseText";
import {
  writeDictationVault,
  clearDictationVault,
  vaultWorthRestoring,
} from "../../utils/dictationVault";
import "./style.css";
import { runFeedbackFlow } from "../../utils/runFeedbackFlow";
const DEFAULT_TAGS = [];
const TE_DRAFT_ID = "text-editor-modal";

// Shared with HoyDesk — same robot instructions across capture surfaces.
const AGENT_PROMPT_KEY = "hoyDesk.agentPrompt";
const AGENT_PANEL_KEY = "hoyDesk.agentPanelOpen";
const AGENT_AUTO_KEY = "hoyDesk.agentAutoMode";
const AGENT_ROBOT_PH = "Instrucciones fijas (ej. laconic). Copiar las junta con el dictado. No se borran al despejar.";
const CAPTURE_ROBOT_PH = "Dictado / mensaje. Copiar envía instrucciones + esto.";
const AGENT_AUTO_PH = "Instrucciones fijas. Se repiten en cada Copiar. Abajo se limpia tras copiar.";
const ROBOT_DOUBLE_TAP_MS = 320;

function readAgentPrompt() {
  try {
    return localStorage.getItem(AGENT_PROMPT_KEY) || "";
  } catch {
    return "";
  }
}

function readAgentAutoMode() {
  try {
    return localStorage.getItem(AGENT_AUTO_KEY) === "1";
  } catch {
    return false;
  }
}

function readAgentPanelOpen() {
  try {
    return localStorage.getItem(AGENT_PANEL_KEY) === "1" || readAgentAutoMode();
  } catch {
    return false;
  }
}

function buildCaptureCopyText(agentPrompt, mainInput) {
  const agent = String(agentPrompt ?? "").trim();
  const main = String(mainInput ?? "");
  if (agent && main) return `${agent}\n\n${main}`;
  return agent || main;
}

function InterimConfidenceWords({ payload }) {
  if (!payload) return null;
  if (typeof payload === "string") {
    return <span className="text-editor-voice-text">{payload}</span>;
  }
  const words = Array.isArray(payload.words) ? payload.words : [];
  if (!words.length) {
    return <span className="text-editor-voice-text">{dictationText(payload)}</span>;
  }
  return (
    <span className="text-editor-voice-text" style={{ display: "inline" }}>
      {words.map((token, index) => {
        const word = token?.punctuated_word || token?.word || "";
        if (!word) return null;
        return (
          <React.Fragment key={`${index}-${word}`}>
            {index > 0 ? " " : null}
            <span title={confidenceTitle(token.confidence)} style={confidenceStyle(token.confidence)}>
              {word}
            </span>
          </React.Fragment>
        );
      })}
    </span>
  );
}

export default function TextEditor({
  isOpen,
  onClose,
  onSave,
  initialTitle = "",
  initialContent = "",
  initialImg = "",
  initialTags = DEFAULT_TAGS,
  mode = "create",
  autoStartRecording = false,
}) {
  const { canEdit } = useEditLock();
  const locked = !canEdit;

  // Combined content state - first line IS the title
  const [content, setContent] = useState("");
  const [img, setImg] = useState(initialImg);
  const [tags, setTags] = useState(initialTags);
  const [tagInput, setTagInput] = useState("");
  const [isMagicLoading, setIsMagicLoading] = useState(false);
  const [magicMenuOpen, setMagicMenuOpen] = useState(false);
  const [magicResult, setMagicResult] = useState(null);
  // Snapshot of pre-magic content/img — restore is one click away
  const [magicSnapshot, setMagicSnapshot] = useState(null);
  const [interimPayload, setInterimPayload] = useState(null);
  const [dictationLang, setDictationLang] = useState("es");
  const [scrambleNoise, setScrambleNoise] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const { settings } = useWorkspace();
  const voiceStyle = getVoiceCaptureStyle(settings);
  const useCryptic = voiceStyle === "cryptic";
  const [showPreview, setShowPreview] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [draftStatus, setDraftStatus] = useState("");
  const [vaultBanner, setVaultBanner] = useState(null);
  const [agentPrompt, setAgentPrompt] = useState(readAgentPrompt);
  const [agentPanelOpen, setAgentPanelOpen] = useState(readAgentPanelOpen);
  const [agentAutoMode, setAgentAutoMode] = useState(readAgentAutoMode);
  const [copyFlash, setCopyFlash] = useState(false);

  const magicWrapRef = useRef(null);

  const overlayRef = useRef(null);
  const textareaRef = useRef(null);
  const agentPromptRef = useRef(null);
  const robotClickRef = useRef({ lastAt: 0, singleTimer: null });
  const fileInputRef = useRef(null);
  const miniatureInputRef = useRef(null);
  const audioFileInputRef = useRef(null);
  const audioAbortRef = useRef(null);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isAudioDragOver, setIsAudioDragOver] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const {
    isRecording,
    startRecording,
    stopRecording,
    error: voiceError,
    voiceLevel,
  } = useVoiceToText(
    (finalPayload) => {
      const text = dictationText(finalPayload);
      console.log("[TE:VOICE] Final transcript:", text);
      setInterimPayload(null);
      if (!text) return;
      setContent((prev) => appendDictationChunk(prev, text, null));
    },
    (interim) => {
      // Keep rich payload for confidence coloring; strings stay scrambled.
      setInterimPayload(tentativeDisplay(interim) || null);
    },
    { language: dictationLang },
  );

  const insertTextAtCursor = useCallback(
    (text) => {
      const el = textareaRef.current;
      const safe = String(text ?? "");
      if (!safe) return;

      if (!el) {
        setContent((prev) => `${prev}${prev ? "" : ""}${safe}`);
        return;
      }

      const start = el.selectionStart ?? content.length;
      const end = el.selectionEnd ?? content.length;
      const prefix = content.slice(0, start);
      const suffix = content.slice(end);
      setContent(`${prefix}${safe}${suffix}`);

      window.setTimeout(() => {
        try {
          el.focus();
          const pos = start + safe.length;
          el.setSelectionRange(pos, pos);
        } catch (_e) {}
      }, 0);
    },
    [content],
  );

  const handlePasteButton = useCallback(async () => {
    if (locked) return;
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      insertTextAtCursor(text);
      setSaveError(null);
    } catch (e) {
      console.warn("[TextEditor] clipboard.readText failed:", e);
      textareaRef.current?.focus();
      setSaveError("No se pudo leer el portapapeles. Probá con Ctrl/Cmd+V.");
      setTimeout(() => setSaveError(null), 2000);
    }
  }, [insertTextAtCursor, locked]);

  const captureCopyText = useMemo(
    () => buildCaptureCopyText(agentPrompt, content),
    [agentPrompt, content],
  );

  const robotModeTitle = useMemo(() => {
    if (agentAutoMode) return "Auto agente: copiar limpia dictado · doble toque = modo normal";
    if (agentPanelOpen) return "Modo agente · doble toque = auto loop";
    return "Modo agente · doble toque = auto loop";
  }, [agentAutoMode, agentPanelOpen]);

  const agentFieldPlaceholder = agentAutoMode ? AGENT_AUTO_PH : AGENT_ROBOT_PH;

  useEffect(() => {
    try {
      localStorage.setItem(AGENT_PROMPT_KEY, agentPrompt);
    } catch (_e) {}
  }, [agentPrompt]);

  useEffect(() => {
    try {
      localStorage.setItem(AGENT_PANEL_KEY, agentPanelOpen ? "1" : "0");
    } catch (_e) {}
  }, [agentPanelOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(AGENT_AUTO_KEY, agentAutoMode ? "1" : "0");
    } catch (_e) {}
  }, [agentAutoMode]);

  useEffect(() => {
    return () => {
      if (robotClickRef.current.singleTimer) {
        clearTimeout(robotClickRef.current.singleTimer);
      }
    };
  }, []);

  const handleCopyDictation = useCallback(async () => {
    if (locked || !captureCopyText) return;
    try {
      await navigator.clipboard.writeText(captureCopyText);
      setCopyFlash(true);
      setTimeout(() => setCopyFlash(false), 1200);
      if (agentAutoMode) {
        if (content.trim().length >= 12) {
          writeDictationVault(content, { source: "text-editor-auto-copy" });
        }
        setContent("");
        window.setTimeout(() => textareaRef.current?.focus(), 0);
      }
    } catch (e) {
      console.warn("[TextEditor] clipboard.writeText failed:", e);
      setSaveError("No se pudo copiar al portapapeles.");
      setTimeout(() => setSaveError(null), 2000);
    }
  }, [locked, captureCopyText, agentAutoMode, content]);

  const handleDespejar = useCallback(async () => {
    if (locked) return;
    const raw = String(content || "");
    if (!raw.trim()) return;
    if (raw.trim().length >= 40) {
      writeDictationVault(raw, { source: "text-editor-despejar" });
      if (!(await confirmInApp({
        title: "Despejar dictado",
        message: "Las instrucciones del robot se conservan. El borrador local queda por si acaso.",
        confirmLabel: "Despejar",
        danger: true,
      }))) {
        return;
      }
    }
    setContent("");
    setInterimPayload(null);
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  }, [locked, content]);

  const handleRobotModeClick = useCallback(() => {
    if (locked) return;
    const now = Date.now();
    const prev = robotClickRef.current.lastAt;
    robotClickRef.current.lastAt = now;

    if (now - prev < ROBOT_DOUBLE_TAP_MS) {
      if (robotClickRef.current.singleTimer) {
        clearTimeout(robotClickRef.current.singleTimer);
        robotClickRef.current.singleTimer = null;
      }
      setAgentPanelOpen(true);
      setAgentAutoMode((auto) => {
        const next = !auto;
        window.setTimeout(() => {
          (next ? textareaRef : agentPromptRef).current?.focus();
        }, 0);
        return next;
      });
      return;
    }

    robotClickRef.current.singleTimer = window.setTimeout(() => {
      robotClickRef.current.singleTimer = null;
      setAgentPanelOpen((open) => {
        const next = !open;
        if (!next) setAgentAutoMode(false);
        else window.setTimeout(() => agentPromptRef.current?.focus(), 0);
        return next;
      });
    }, ROBOT_DOUBLE_TAP_MS);
  }, [locked]);

  const mergeTranscriptIntoContent = useCallback((transcript) => {
    const t = String(transcript || "").trim();
    if (!t) return;

    setContent((prev) => {
      const prevStr = String(prev || "");
      if (!prevStr.trim()) {
        return `${titleFromTranscript(t)}\n${t}`;
      }

      const lines = prevStr.split("\n");
      const nextTitle = lines[0] ?? "";
      const body = lines.slice(1).join("\n");

      const nextBody = body
        ? `${body}${body.endsWith("\n") ? "" : "\n"}${t}`
        : t;

      return `${nextTitle}\n${nextBody}`;
    });
  }, []);

  const handleTranscribeAudioFile = useCallback(
    async (file) => {
      if (locked) return;
      if (!file) return;

      const validationError = validateVoiceFile(file);
      if (validationError) {
        setSaveError(validationError);
        setTimeout(() => setSaveError(null), 2500);
        return;
      }

      try {
        audioAbortRef.current?.abort();
      } catch (_e) {}

      const controller = new AbortController();
      audioAbortRef.current = controller;

      setIsAudioLoading(true);
      setSaveError(null);

      try {
        const { transcript } = await transcribeAudioFile(file, {
          signal: controller.signal,
        });
        if (!transcript) return;
        mergeTranscriptIntoContent(transcript);
      } catch (e) {
        if (e?.name === "AbortError") return;
        console.warn("[TextEditor] audio transcribe failed:", e);
        setSaveError("No se pudo transcribir el audio.");
        setTimeout(() => setSaveError(null), 2500);
      } finally {
        setIsAudioLoading(false);
      }
    },
    [locked, mergeTranscriptIntoContent],
  );

  const handleAudioInputChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";
      await handleTranscribeAudioFile(file);
    },
    [handleTranscribeAudioFile],
  );

  const handleAudioDrop = useCallback(
    async (e) => {
      e.preventDefault();
      setIsAudioDragOver(false);
      if (locked) return;
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      await handleTranscribeAudioFile(file);
    },
    [handleTranscribeAudioFile, locked],
  );

  const handleAudioDragOver = useCallback(
    (e) => {
      if (locked) return;
      e.preventDefault();
      const file = e.dataTransfer?.files?.[0];
      if (!file) {
        setIsAudioDragOver(false);
        return;
      }
      setIsAudioDragOver(!validateVoiceFile(file));
    },
    [locked],
  );

  const handleAudioDragLeave = useCallback(() => {
    setIsAudioDragOver(false);
  }, []);

  // Auto-start dictation when the unified Mic entry opens this modal.
  const didAutoStartRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      didAutoStartRef.current = false;
      return;
    }
    if (!autoStartRecording) return;
    if (locked) return;
    if (isRecording) return;
    if (didAutoStartRef.current) return;
    didAutoStartRef.current = true;
    startRecording();
  }, [isOpen, autoStartRecording, locked, isRecording, startRecording]);

  useEffect(() => {
    if (!magicMenuOpen) return;
    const onDown = (e) => {
      const wrap = magicWrapRef.current;
      if (!wrap) return;
      if (wrap.contains(e.target)) return;
      setMagicMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown, { capture: true });
    return () => document.removeEventListener("mousedown", onDown, { capture: true });
  }, [magicMenuOpen]);

  useEffect(() => {
    if (!isOpen) stopRecording();
  }, [isOpen, stopRecording]);

  // Cryptic scramble only when that style is selected (default is wave).
  useEffect(() => {
    if (!isRecording || !useCryptic) {
      setScrambleNoise("");
      return;
    }
    const chars = "ABCDEFGHIKLMNOPQRSTUVWXYZ0123456789⟁⏚⏣⌬⍟⚿⛫⛬";
    const interval = setInterval(() => {
      if (interimPayload) {
        setScrambleNoise("");
        return;
      }
      let noise = "";
      for (let i = 0; i < 15; i++) {
        noise += chars.charAt(Math.floor(Math.random() * chars.length)) + " ";
      }
      setScrambleNoise(noise);
    }, 80);
    return () => clearInterval(interval);
  }, [isRecording, interimPayload, useCryptic]);

  // Sync with props ONLY when the modal opens — never while dictating.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      console.log("[TE] Modal opening");
      const combinedContent = initialTitle
        ? initialTitle + "\n" + initialContent
        : initialContent;
      setContent(combinedContent || "");
      setImg(initialImg);
      setTags(initialTags || []);
      setTagInput("");
      setShowPreview(false);
      setSaveError(null);
      setMagicSnapshot(null);
      setMagicResult(null);

      const vault = vaultWorthRestoring({ minChars: 12 });
      if (vault && !(combinedContent || "").trim()) {
        setVaultBanner(vault);
      } else if (vault && vault.text.trim().length > (combinedContent || "").trim().length + 40) {
        setVaultBanner(vault);
      } else {
        setVaultBanner(null);
      }
    }
    if (!isOpen && wasOpenRef.current) {
      setVaultBanner(null);
    }
    wasOpenRef.current = isOpen;
  }, [initialTitle, initialContent, initialImg, initialTags, isOpen]);

  useDraftAutosave({
    draftId: isOpen ? TE_DRAFT_ID : "",
    locked,
    title: "",
    content,
    deskId: "",
    drawerId: "",
    notebookId: "",
    onStatus: setDraftStatus,
  });

  useNeverLoseText({ enabled: isOpen && !locked, text: content, source: "text-editor" });

  // Keep vault warm from the editor body too (covers paste / typing, not only mic).
  useEffect(() => {
    if (!isOpen || locked) return;
    if (!content.trim() || content.trim().length < 12) return;
    writeDictationVault(content, { source: "text-editor" });
  }, [content, isOpen, locked]);

  // Auto-focus textarea when opening
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => textareaRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Extract title from first line
  const extractTitle = useCallback(() => {
    const lines = content.split("\n");
    const firstLine = lines[0].trim();
    return firstLine || "Sin título";
  }, [content]);

  // Extract body (everything after first line)
  const extractBody = useCallback(() => {
    const lines = content.split("\n");
    if (lines.length <= 1) return "";
    return lines.slice(1).join("\n");
  }, [content]);

  const handleSave = useCallback(async () => {
    if (locked) return;
    let title = extractTitle();
    let body = extractBody();

    if (!body.trim() && content.trim()) {
      const lines = content.split("\n");
      if (lines.length === 1) {
        title = titleFromPaste(content);
        body = content.trim();
      } else {
        title = lines[0].trim() || titleFromPaste(content);
        body = lines.slice(1).join("\n").trim() || content.trim();
      }
    }

    if (!title || !body.trim()) {
      setSaveError("Primera línea = título. El resto = cuerpo. Pegá el artículo o agregá un salto después del título.");
      textareaRef.current?.focus();
      return;
    }

    setSaveError(null);
    try {
      await Promise.resolve(onSave(title, body, img.trim(), tags));
      clearDictationVault();
      clearDraftStorage(TE_DRAFT_ID);
      // onSave is expected to close the modal upstream; keep editor alive on failure.
    } catch (e) {
      const msg = e?.message || "No se pudo guardar.";
      setSaveError(msg);
      writeDictationVault(content, { source: "text-editor-save-failed" });
    }
  }, [content, img, tags, extractTitle, extractBody, onSave, locked]);

  const requestClose = useCallback(async () => {
    if (content.trim()) {
      writeDictationVault(content, { source: "text-editor-close" });
      if (!(await confirmInApp({
        title: "Cerrar sin guardar",
        message: "El borrador local se conserva.",
        confirmLabel: "Cerrar",
        danger: true,
      }))) return;
    }
    onClose();
  }, [content, onClose]);

  const addTag = () => {
    const val = tagInput.trim().toLowerCase().replace(/^#/, "");
    if (val && !tags.includes(val)) {
      setTags([...tags, val]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const snapshotOriginal = useCallback(() => {
    setMagicSnapshot((prev) => prev ?? { content, img });
  }, [content, img]);

  const restoreOriginal = useCallback(() => {
    if (!magicSnapshot) return;
    setContent(magicSnapshot.content);
    setImg(magicSnapshot.img);
    setMagicSnapshot(null);
    setMagicResult({ type: "restore", message: "Original restaurado" });
    setTimeout(() => setMagicResult(null), 2500);
  }, [magicSnapshot]);

  const handleMagicPolish = async () => {
    if (!content.trim()) return;
    setIsMagicLoading(true);
    setMagicMenuOpen(false);
    try {
      await runFeedbackFlow({
        actionId: "ai-polish",
        label: "IA",
        dbStep: async () => {
          const data = await enhanceNote({
            text: content,
            title: extractTitle(),
          });
          if (!data?.correctedText) throw new Error("Sin texto pulido");
          snapshotOriginal();
          const t = data.title || extractTitle();
          const body = data.correctedText.trim();
          const first = body.split("\n")[0]?.trim();
          setContent(first === t ? body : `${t}\n\n${body}`);
          if (data.imageDataUrl) setImg(data.imageDataUrl);
          setMagicResult({
            type: "polish",
            message: t ? `Pulido · ${t}` : "Nota pulida",
          });
          setTimeout(() => setMagicResult(null), 3000);
        },
        successMessage: "IA OK",
        errorMessage: "La magia falló",
      });
    } catch (err) {
      console.error(err);
      await confirmInApp({
        title: "Error",
        message: err.message || "La magia falló al pulir.",
        confirmLabel: "Cerrar",
        danger: true,
      });
    } finally {
      setIsMagicLoading(false);
    }
  };

  const handleMagicCorrect = async () => {
    if (!content.trim()) return;
    setIsMagicLoading(true);
    setMagicMenuOpen(false);
    try {
      await runFeedbackFlow({
        actionId: "ai-correct",
        label: "IA",
        dbStep: async () => {
          const data = await callGeminiAPI({ text: content, mode: "correct" });
          if (!data?.correctedText) throw new Error(data?.message || "Sin texto corregido");
          snapshotOriginal();
          setContent(data.correctedText);
          setMagicResult({ type: "correct", message: "Texto corregido" });
          setTimeout(() => setMagicResult(null), 3000);
        },
        successMessage: "IA OK",
        errorMessage: "La magia falló",
      });
    } catch (err) {
      console.error(err);
      await confirmInApp({
        title: "Error",
        message: err.message || "La magia falló al corregir.",
        confirmLabel: "Cerrar",
        danger: true,
      });
    } finally {
      setIsMagicLoading(false);
    }
  };

  const handleMagicTitle = async () => {
    if (!content.trim()) return;
    setIsMagicLoading(true);
    setMagicMenuOpen(false);
    try {
      await runFeedbackFlow({
        actionId: "ai-title",
        label: "IA",
        dbStep: async () => {
          const data = await callGeminiAPI({
            text: content,
            title: extractTitle(),
            mode: "title",
          });
          if (!data?.suggestedTitle) throw new Error(data?.message || "Sin título sugerido");
          snapshotOriginal();
          const lines = content.split("\n");
          lines[0] = data.suggestedTitle;
          setContent(lines.join("\n"));
          setMagicResult({
            type: "title",
            message: `Título: "${data.suggestedTitle}"`,
          });
          setTimeout(() => setMagicResult(null), 3000);
        },
        successMessage: "IA OK",
        errorMessage: "La magia falló",
      });
    } catch (err) {
      console.error(err);
      await confirmInApp({
        title: "Error",
        message: err.message || "La magia falló al sugerir título.",
        confirmLabel: "Cerrar",
        danger: true,
      });
    } finally {
      setIsMagicLoading(false);
    }
  };

  const handleMagicImage = async () => {
    if (!content.trim()) return;
    setIsMagicLoading(true);
    setMagicMenuOpen(false);
    try {
      await runFeedbackFlow({
        actionId: "ai-image",
        label: "IA",
        dbStep: async () => {
          const data = await callGeminiAPI({
            text: content,
            title: extractTitle(),
            mode: "image",
          });
          if (!data?.imageData) throw new Error(data?.message || "No se pudo generar la imagen.");
          snapshotOriginal();
          setImg(`data:${data.mimeType};base64,${data.imageData}`);
          setMagicResult({ type: "image", message: "Imagen Art Deco generada" });
          setTimeout(() => setMagicResult(null), 3000);
        },
        successMessage: "IA OK",
        errorMessage: "La magia falló",
      });
    } catch (err) {
      console.error(err);
      await confirmInApp({
        title: "Error",
        message: err.message || "La magia falló al generar imagen.",
        confirmLabel: "Cerrar",
        danger: true,
      });
    } finally {
      setIsMagicLoading(false);
    }
  };

  const handleImageInsert = useCallback(
    async (filesOrDataUrl) => {
      const textarea = textareaRef.current;
      const start = textarea?.selectionStart ?? content.length;
      const end = textarea?.selectionEnd ?? content.length;

      let insertion = "";
      if (typeof filesOrDataUrl === "string") {
        insertion = `\n${buildInlineImgTag(filesOrDataUrl)}\n`;
      } else {
        const files = extractImageFilesFromFileList(filesOrDataUrl);
        if (!files.length) return;
        const tags = [];
        for (let i = 0; i < files.length; i++) {
          const base64 = await readFileAsDataUrl(files[i]);
          const alt = files.length > 1 ? `Imagen ${i + 1}` : "Imagen";
          tags.push(buildInlineImgTag(base64, alt));
        }
        insertion = `\n${tags.join("\n")}\n`;
      }

      const newContent =
        content.substring(0, start) + insertion + content.substring(end);
      setContent(newContent);

      setTimeout(() => {
        if (!textarea) return;
        textarea.focus();
        const newPos = start + insertion.length;
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    },
    [content],
  );

  const handleFileUpload = useCallback(
    (e, target = "content") => {
      const files = extractImageFilesFromFileList(e.target.files);
      if (!files.length) return;

      if (target === "miniature") {
        const reader = new FileReader();
        reader.onload = (event) => {
          // RTDB validates thumbnails at <= 8,000 chars. Store a small thumbnail.
          const image = new Image();
          image.onload = () => {
            const maxSide = 320;
            const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(image.width * scale));
            canvas.height = Math.max(1, Math.round(image.height * scale));
            canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
            setImg(canvas.toDataURL("image/jpeg", 0.65));
          };
          image.src = event.target.result;
        };
        reader.readAsDataURL(files[0]);
      } else {
        handleImageInsert(files);
      }
      e.target.value = "";
    },
    [handleImageInsert],
  );

  const handlePaste = useCallback(
    async (e) => {
      console.log("[TE] onPaste triggered");
      const imageFiles = extractImageFilesFromClipboard(e.clipboardData);
      if (imageFiles.length) {
        e.preventDefault();
        await handleImageInsert(imageFiles);
        return;
      }

      const plain = e.clipboardData?.getData("text/plain");
      if (!plain || locked) return;

      if (!img || img.includes("google.com/s2/favicons")) {
        resolveLinkThumb(plain).then((thumb) => {
          if (thumb) setImg(thumb);
        });
      }

      if (!isPasteDump(plain)) return;

      e.preventDefault();
      const block = smartPaste(plain);
      const el = textareaRef.current;
      const start = el?.selectionStart ?? content.length;
      const end = el?.selectionEnd ?? content.length;
      const prefix = content.slice(0, start);
      const suffix = content.slice(end);
      const glue = prefix && !prefix.endsWith("\n") ? "\n" : "";
      setContent(`${prefix}${glue}${block}${suffix.startsWith("\n") ? "" : "\n"}${suffix}`);
    },
    [content, handleImageInsert, img, locked],
  );

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") {
        requestClose();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        if (!locked) handleSave();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, requestClose, handleSave, locked]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) requestClose();
  };

  const title = extractTitle();
  const isFirstLineBold = title.length > 0 && content.includes("\n");

  return (
    <div
      ref={overlayRef}
      className="text-editor-overlay"
      onClick={handleOverlayClick}
    >
      <div className="text-editor-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="text-editor-header">
          <div className="text-editor-header-left">
            <button
              onClick={() => miniatureInputRef.current?.click()}
              className="text-editor-thumbnail-btn"
              title="Imagen destacada"
              disabled={locked}
            >
              {img ? (
                <div className="text-editor-thumbnail-preview">
                  <img src={img} alt="Miniatura" />
                </div>
              ) : (
                <div className="text-editor-thumbnail-placeholder">
                  <ImageIcon size={isMobile ? 16 : 20} />
                </div>
              )}
            </button>

            <div className="text-editor-title-preview">
              <span className="text-editor-title-text">
                {title || "Nuevo manuscrito"}
              </span>
              <span className="text-editor-tag-count">
                {tags.length > 0 ? `${tags.length} etiquetas` : "Sin etiquetas"}
              </span>
            </div>
          </div>

          <div className="text-editor-header-actions">
            <button
              className="text-editor-header-btn save-btn"
              onClick={handleSave}
              title="Guardar (Ctrl+Enter)"
              disabled={locked}
            >
              <Save size={isMobile ? 16 : 18} />
              {!isMobile && <span>Guardar</span>}
            </button>
            <button
              className="text-editor-header-btn close-btn"
              onClick={requestClose}
              title="Cerrar"
            >
              <X size={isMobile ? 16 : 18} />
            </button>
          </div>
        </div>

        {/* Toolbar - Compact for mobile */}
        <div className="text-editor-toolbar">
          <div className="text-editor-toolbar-left">
            <DictationLangToggle
              value={dictationLang}
              disabled={locked}
              onChange={(lang) => {
                if (locked) return;
                setDictationLang(lang);
                if (isRecording) {
                  stopRecording();
                  setTimeout(startRecording, 120);
                }
              }}
            />

            <button
              onClick={isRecording ? stopRecording : startRecording}
              className={`text-editor-toolbar-btn ${isRecording ? "recording" : ""}`}
              title={isRecording ? "Detener Dictado" : "Dictar Texto"}
              disabled={locked}
            >
              {isRecording ? (
                <MicOff size={isMobile ? 14 : 16} />
              ) : (
                <Mic size={isMobile ? 14 : 16} />
              )}
              {!isMobile && (
                <span>{isRecording ? "Escuchando..." : "Dictar"}</span>
              )}
            </button>

            <div className="text-editor-magic-container" ref={magicWrapRef}>
              <button
                onClick={() => setMagicMenuOpen(!magicMenuOpen)}
                disabled={locked || isMagicLoading}
                className="text-editor-toolbar-btn magic-btn"
                title="Magia AI"
              >
                {isMagicLoading ? (
                  <Loader2 size={isMobile ? 14 : 16} className="animate-spin" />
                ) : (
                  <Sparkles size={isMobile ? 14 : 16} />
                )}
                {!isMobile && (
                  <span>{isMagicLoading ? "Invocando..." : "Magia"}</span>
                )}
              </button>

              {magicMenuOpen && (
                <div className="text-editor-magic-menu">
                  <button
                    onClick={handleMagicPolish}
                    className="magic-menu-item"
                    disabled={locked}
                  >
                    <Sparkles size={16} />
                    <span>Pulir nota</span>
                  </button>
                  <button
                    onClick={handleMagicCorrect}
                    className="magic-menu-item"
                    disabled={locked}
                  >
                    <Wand2 size={16} />
                    <span>Corregir texto</span>
                  </button>
                  <button
                    onClick={handleMagicTitle}
                    className="magic-menu-item"
                    disabled={locked}
                  >
                    <Type size={16} />
                    <span>Sugerir título</span>
                  </button>
                  <button
                    onClick={handleMagicImage}
                    className="magic-menu-item"
                    disabled={locked}
                  >
                    <Palette size={16} />
                    <span>Generar imagen</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="text-editor-toolbar-right">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-editor-toolbar-btn"
              title="Insertar imagen"
              disabled={locked}
            >
              <Upload size={isMobile ? 14 : 16} />
              {!isMobile && <span>Imagen</span>}
            </button>

            <button
              onClick={handlePasteButton}
              className="text-editor-toolbar-btn"
              title="Pegar del portapapeles"
              disabled={locked}
            >
              <ClipboardPaste size={isMobile ? 14 : 16} />
              {!isMobile && <span>Pegar</span>}
            </button>

            <button
              onClick={() => audioFileInputRef.current?.click()}
              className="text-editor-toolbar-btn"
              title="Subir audio para transcribir"
              disabled={locked || isAudioLoading}
            >
              {isAudioLoading ? (
                <Loader2 size={isMobile ? 14 : 16} className="animate-spin" />
              ) : (
                <Mic size={isMobile ? 14 : 16} />
              )}
              {!isMobile && <span>Audio</span>}
            </button>

            {isMobile && (
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="text-editor-toolbar-btn"
              >
                {showPreview ? <Type size={14} /> : <ImageIcon size={14} />}
              </button>
            )}
          </div>
        </div>

        {/* Tags Bar - Horizontal scroll for mobile */}
        <div className="text-editor-tags-bar">
          <div className="text-editor-tags-list">
            {["código", "iglesia", "vida", "cuentos"].map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  if (!tags.includes(cat)) {
                    setTags([...tags, cat]);
                  } else {
                    setTags(tags.filter((t) => t !== cat));
                  }
                }}
                className={`text-editor-tag-chip ${tags.includes(cat) ? "active" : ""}`}
                  disabled={locked}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}

            {tags
              .filter(
                (t) => !["código", "iglesia", "vida", "cuentos"].includes(t),
              )
              .map((t) => (
                <span key={t} className="text-editor-tag-chip active">
                  #{t}
                  <button
                    onClick={() => removeTag(t)}
                    className="tag-remove-btn"
                    disabled={locked}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
          </div>

          <div className="text-editor-tag-input-container">
            <input
              type="text"
              placeholder="+ etiqueta"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTag()}
              className="text-editor-tag-input"
              disabled={locked}
            />
            <button onClick={addTag} className="text-editor-tag-add-btn" disabled={locked}>
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Content Area - Main focus */}
        <div className="text-editor-content-area">
          {agentPanelOpen && (
            <textarea
              ref={agentPromptRef}
              className="text-editor-agent-prompt"
              rows={2}
              value={agentPrompt}
              onChange={(e) => setAgentPrompt(e.target.value)}
              placeholder={agentFieldPlaceholder}
              aria-label="Instrucciones para agente"
              disabled={locked}
            />
          )}
          {vaultBanner && (
            <div
              className="text-editor-toast"
              style={{
                position: "relative",
                margin: "0 0 8px",
                background: "rgba(212,175,55,0.18)",
                border: "1px solid rgba(212,175,55,0.45)",
                color: "#F5E6A8",
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <span style={{ flex: 1, fontSize: "0.85rem" }}>
                Borrador de dictado recuperable ({vaultBanner.text.trim().length} caracteres).
              </span>
              <button
                type="button"
                className="text-editor-footer-btn save-btn"
                style={{ padding: "4px 10px", fontSize: "0.8rem" }}
                onClick={() => {
                  setContent((prev) => {
                    const p = String(prev || "").trim();
                    if (!p) return vaultBanner.text;
                    if (p.includes(vaultBanner.text.trim())) return prev;
                    return `${prev.trim()}\n\n${vaultBanner.text.trim()}`;
                  });
                  setVaultBanner(null);
                }}
              >
                Restaurar
              </button>
              <button
                type="button"
                className="text-editor-footer-btn cancel-btn"
                style={{ padding: "4px 10px", fontSize: "0.8rem" }}
                onClick={() => setVaultBanner(null)}
              >
                Ignorar
              </button>
            </div>
          )}
          <div
            className="text-editor-textarea-container"
            onDragOver={handleAudioDragOver}
            onDragLeave={handleAudioDragLeave}
            onDrop={handleAudioDrop}
            style={
              isAudioDragOver
                ? {
                    outline: "2px dashed rgba(212, 175, 55, 0.7)",
                    outlineOffset: "2px",
                  }
                : undefined
            }
          >
            <textarea
              ref={textareaRef}
              placeholder={
                agentPanelOpen
                  ? `${CAPTURE_ROBOT_PH}\n\nPrimera línea = título del manuscrito.`
                  : `Comience a escribir aquí...\nLa primera línea será el título del manuscrito.\n\nPuede pegar imágenes directamente o usar el botón de carga.`
              }
              value={content}
              onChange={(e) => {
                const next = e.target.value;
                // Accidental select-all + type can wipe a long dictation; vault already has a copy.
                if (!next.trim() && content.trim().length >= 40) {
                  writeDictationVault(content, { source: "text-editor-clear-guard" });
                }
                setContent(next);
              }}
              onPaste={locked ? undefined : handlePaste}
              readOnly={locked}
              className="text-editor-textarea"
            />

            {/* Title hint on first line */}
            {isFirstLineBold && (
              <div className="text-editor-title-hint">
                ↑ Título del manuscrito
              </div>
            )}

            {/* Voice overlay — interim transcript; cryptic listening chrome if selected */}
            {(interimPayload || (isRecording && useCryptic)) && (
              <div className="text-editor-voice-overlay">
                {interimPayload ? (
                  <InterimConfidenceWords payload={interimPayload} />
                ) : (
                  <span className="text-editor-scramble">{scrambleNoise}</span>
                )}
              </div>
            )}
          </div>
          {!useCryptic && <VoiceFrequencyBars active={isRecording} level={voiceLevel} />}

          {/* Image preview for mobile */}
          {isMobile && showPreview && img && (
            <div className="text-editor-image-preview">
              <img src={img} alt="Preview" />
            </div>
          )}
        </div>

        {/* Original one click away after any magic apply */}
        {magicSnapshot && (
          <div className="text-editor-magic-restore">
            <span>Original guardado</span>
            <button
              type="button"
              onClick={restoreOriginal}
              className="text-editor-magic-restore-btn"
              disabled={locked}
              title="Volver al texto/imagen original"
            >
              <Undo2 size={14} />
              Restaurar original
            </button>
          </div>
        )}

        {/* Toast messages */}
        {magicResult && (
          <div className="text-editor-toast success">
            ✨ {magicResult.message}
          </div>
        )}

        {voiceError && (
          <div className="text-editor-toast error">🎙️ {voiceError}</div>
        )}

        {saveError && (
          <div className="text-editor-toast error">{saveError}</div>
        )}

        {/* Footer */}
        <div className="text-editor-footer">
          <div className="text-editor-footer-primary">
            <button
              type="button"
              className={`text-editor-footer-btn copy-btn${copyFlash ? " copied" : ""}`}
              onClick={handleCopyDictation}
              disabled={locked || !captureCopyText.trim()}
              title={agentAutoMode ? "Copiar y limpiar dictado" : "Copiar dictado (+ instrucciones robot)"}
            >
              <ClipboardCopy size={isMobile ? 22 : 26} />
              <span>{copyFlash ? "¡Copiado!" : "Copiar"}</span>
            </button>
            <button
              type="button"
              className="text-editor-footer-btn despejar-btn"
              onClick={handleDespejar}
              disabled={locked || !content.trim()}
              title="Despejar dictado (conserva instrucciones robot)"
            >
              <Eraser size={isMobile ? 18 : 20} />
              <span>Despejar</span>
            </button>
            <button
              type="button"
              className={`text-editor-footer-btn robot-btn${agentPanelOpen || agentAutoMode ? " active" : ""}${agentAutoMode ? " auto" : ""}`}
              onClick={handleRobotModeClick}
              disabled={locked}
              title={robotModeTitle}
              aria-label={robotModeTitle}
              aria-pressed={agentPanelOpen}
            >
              <span className="text-editor-robot-glyph">
                <span>[</span>
                <AgentBotIcon size={isMobile ? 16 : 18} auto={agentAutoMode} />
                <span>]</span>
              </span>
            </button>
          </div>
          <div className="text-editor-footer-row">
            <div className="text-editor-footer-info">
              {draftStatus && <span style={{ marginRight: 12 }}>{draftStatus}</span>}
              {extractBody().length > 0 && (
                <span>{extractBody().length} caracteres en el cuerpo</span>
              )}
            </div>
            <div className="text-editor-footer-actions">
              <button
                className="text-editor-footer-btn cancel-btn"
                onClick={requestClose}
              >
                Cancelar
              </button>
              <button
                className="text-editor-footer-btn save-btn"
                onClick={handleSave}
                disabled={locked}
              >
                Guardar manuscrito
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFileUpload(e, "content")}
      />
      <input
        ref={miniatureInputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        style={{ display: "none" }}
        onChange={(e) => handleFileUpload(e, "miniature")}
      />

      <input
        ref={audioFileInputRef}
        type="file"
        accept={VOICE_UPLOAD_ACCEPT}
        style={{ display: "none" }}
        onChange={handleAudioInputChange}
      />
    </div>
  );
}
